import { PublicIdSchemas } from '@idc-forms/id'
import { CivilStatus } from '@idc-forms/types/civil-status'
import { Gender } from '@idc-forms/types/gender'
import { z } from 'zod'
import { requiredString } from '../primitives/string'
import { InstantSchema, LocalDateSchema } from '../primitives/time'
import { V2CursorPaginationSchema } from './http'

/**
 * Contrato de la administración v2 de representantes legales. Octavo catálogo del
 * backoffice en v2 y el que cierra el árbol societario (company → society →
 * representative): un representante cuelga de una sociedad, así que el listado
 * joinea a societies (`$lookup`, el mismo molde relacional que estrenaron
 * agencias/sociedades/proyectos y que properties llevó a dos relaciones).
 *
 * Regla de dominio 1:1: una sociedad tiene A LO SUMO un representante legal vivo
 * (`societyId` es único en la colección). Crear o mover un representante a una
 * sociedad ya representada devuelve `400 ERROR_REPRESENTATIVE_SOCIETY_ALREADY_HAS_ONE`.
 *
 * Los textos legales (name, position, profession, address, notary y los campos del
 * nombramiento) viajan NORMALIZADOS a MAYÚSCULAS: alimentan los PDFs de FEIC,
 * promesa y negociación. `identification` (DPI) y los códigos ISO (`nationality`,
 * `address.country`) NO se recortan a mayúsculas: no son prosa legal.
 *
 * `default` del schema Mongo se OMITE del wire a propósito: es un campo muerto —la
 * regla 1:1 hace que nunca haya un «predeterminado entre varios»—, así que
 * exponerlo sólo agregaría ruido de sistema sin valor de navegación. `blocked` y
 * `active` sí son de sistema y salen read-only. `blocked` NO entra al input: es la
 * marca que protege del borrado y si viajara, cualquiera se desbloquearía antes de
 * borrar.
 */

/** Texto legal del wire de entrada: requerido, recortado y en MAYÚSCULAS. */
const legalText = () =>
  requiredString()
    .transform((value) => value.trim().toUpperCase())
    .pipe(requiredString())

/**
 * Referencia a la sociedad dueña, wire-safe: el `_id` no cruza el adapter, así que
 * se publica su `soc_` junto al nombre. Nullable por robustez del listado: una fila
 * cuya sociedad perdió el `publicId` presenta `null` en vez de tumbar la página
 * entera; `societyId` es required en el dominio, así que en la práctica siempre se
 * resuelve.
 */
export const V2AdminRepresentativeSocietySchema = z
  .object({
    id: PublicIdSchemas.society,
    name: z.string()
  })
  .nullable()

/**
 * Autoría wire-safe: el `_id` del usuario no cruza el adapter, así que se publica su
 * `usr_`. Va entera nullable porque el legacy guarda `createdBy: null` en las filas
 * sembradas, y la UI hace mucho que resuelve ese caso a «—».
 */
export const V2AdminRepresentativeAuthorSchema = z
  .object({
    id: PublicIdSchemas.user,
    fullname: z.string()
  })
  .nullable()

/** Domicilio del representante en el wire de salida (sin la marca `deleted` de Mongo). */
export const V2AdminRepresentativeAddressSchema = z.object({
  address: z.string(),
  /** Zona (sólo aplica en GT); `null` cuando no se cargó. */
  zone: z.number().nullable(),
  /** Código ISO del país. */
  country: z.string(),
  department: z.string().nullable(),
  municipality: z.string().nullable()
})

/** Notario que autoriza el nombramiento. */
export const V2AdminRepresentativeNotarySchema = z.object({
  name: z.string(),
  gender: z.nativeEnum(Gender)
})

/** Datos del nombramiento inscrito en el Registro Mercantil. */
export const V2AdminRepresentativeAppointmentSchema = z.object({
  date: LocalDateSchema,
  number: z.string(),
  sheet: z.string(),
  book: z.string()
})

/** Fila del listado y del detalle. */
export const V2AdminRepresentativeSchema = z.object({
  id: PublicIdSchemas.representative,
  society: V2AdminRepresentativeSocietySchema,
  name: z.string(),
  identification: z.string(),
  gender: z.nativeEnum(Gender),
  civilStatus: z.nativeEnum(CivilStatus),
  position: z.string(),
  profession: z.string(),
  birthdate: LocalDateSchema,
  /** Código ISO del país de nacionalidad. */
  nationality: z.string(),
  address: V2AdminRepresentativeAddressSchema,
  notary: V2AdminRepresentativeNotarySchema,
  appointmentMercantileRegister: V2AdminRepresentativeAppointmentSchema,
  /** De sistema: un representante bloqueado no se puede borrar. Sólo lectura. */
  blocked: z.boolean(),
  active: z.boolean(),
  createdBy: V2AdminRepresentativeAuthorSchema,
  updatedBy: V2AdminRepresentativeAuthorSchema,
  createdAt: InstantSchema,
  updatedAt: InstantSchema
})

/** Página cursor-paginada del listado (ADR 0019). */
export const V2AdminRepresentativesPageSchema = z.object({
  data: z.array(V2AdminRepresentativeSchema),
  pagination: V2CursorPaginationSchema
})

/**
 * Total y faceta `active`, con los mismos filtros allowlist del listado. La faceta
 * ignora el filtro de su propia dimensión; `total` respeta la consulta completa. La
 * sociedad NO es faceta: se filtra por nombre (texto), no cuenta por valor.
 */
export const V2AdminRepresentativesSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  active: z.object({
    true: z.number().int().nonnegative(),
    false: z.number().int().nonnegative()
  })
})

/**
 * Entrada mínima del selector de representantes. Sin cascada por sociedad: la regla
 * 1:1 hace que un representante identifique unívocamente a su sociedad, así que el
 * selector se filtra por nombre en el cliente.
 */
export const V2AdminRepresentativeLookupItemSchema = z.object({
  id: PublicIdSchemas.representative,
  name: z.string()
})

export const V2AdminRepresentativeLookupSchema = z.object({
  data: z.array(V2AdminRepresentativeLookupItemSchema)
})

/** Domicilio en el wire de entrada: los textos legales viajan en MAYÚSCULAS. */
export const V2CreateAdminRepresentativeAddressSchema = z.object({
  address: legalText(),
  zone: z.number().optional(),
  /** Código ISO del país (no se recorta a mayúsculas). */
  country: requiredString(),
  department: z.string().trim().optional(),
  municipality: z.string().trim().optional()
})

export const V2CreateAdminRepresentativeNotarySchema = z.object({
  name: legalText(),
  gender: z.nativeEnum(Gender, {
    errorMap: () => ({ message: 'Debe seleccionar un género' })
  })
})

export const V2CreateAdminRepresentativeAppointmentSchema = z.object({
  date: LocalDateSchema,
  number: legalText(),
  sheet: legalText(),
  book: legalText()
})

/**
 * Alta de representante. La sociedad entra por su id público (`soc_`), no por
 * ObjectId como el wire legacy; el service la resuelve y valida que exista
 * (`400 ERROR_SOCIETY_NOT_FOUND`) y que no tenga ya representante
 * (`400 ERROR_REPRESENTATIVE_SOCIETY_ALREADY_HAS_ONE`). Los subdocumentos viajan
 * COMPLETOS (no hay merge profundo). `blocked` es de sistema y no se acepta.
 */
export const V2CreateAdminRepresentativeSchema = z.object({
  society: PublicIdSchemas.society,
  name: legalText(),
  identification: requiredString(),
  gender: z.nativeEnum(Gender, {
    errorMap: () => ({ message: 'Debe seleccionar un género' })
  }),
  civilStatus: z.nativeEnum(CivilStatus, {
    errorMap: () => ({ message: 'Debe seleccionar un estado civil' })
  }),
  position: legalText(),
  profession: legalText(),
  birthdate: LocalDateSchema,
  /** Código ISO del país de nacionalidad. */
  nationality: requiredString(),
  address: V2CreateAdminRepresentativeAddressSchema,
  notary: V2CreateAdminRepresentativeNotarySchema,
  appointmentMercantileRegister: V2CreateAdminRepresentativeAppointmentSchema,
  active: z.boolean().optional()
})

/**
 * Edición parcial a nivel raíz: un id desconocido responde 404 (nunca hace upsert).
 * Los subdocumentos, si vienen, vienen COMPLETOS (semántica del `$set`, sin merge
 * profundo). Mover a otra sociedad (`soc_`) re-chequea la regla 1:1.
 */
export const V2UpdateAdminRepresentativeSchema = V2CreateAdminRepresentativeSchema.partial()

export type V2AdminRepresentativeSociety = z.infer<typeof V2AdminRepresentativeSocietySchema>
export type V2AdminRepresentativeAuthor = z.infer<typeof V2AdminRepresentativeAuthorSchema>
export type V2AdminRepresentative = z.infer<typeof V2AdminRepresentativeSchema>
export type V2AdminRepresentativesPage = z.infer<typeof V2AdminRepresentativesPageSchema>
export type V2AdminRepresentativesSummary = z.infer<typeof V2AdminRepresentativesSummarySchema>
export type V2AdminRepresentativeLookupItem = z.infer<typeof V2AdminRepresentativeLookupItemSchema>
export type V2AdminRepresentativeLookup = z.infer<typeof V2AdminRepresentativeLookupSchema>
export type V2CreateAdminRepresentativeInput = z.infer<typeof V2CreateAdminRepresentativeSchema>
export type V2UpdateAdminRepresentativeInput = z.infer<typeof V2UpdateAdminRepresentativeSchema>
